import { GetServerSideProps } from "next";
import { IHTMLSection } from "@/components/Content/interfaces";
import Section from "@/components/Content/Section";
import { getStaticPageData } from "@/helpers/api/service/Staticpage";
import { IStaticPageSEO } from "@/components/Head/interfaces";
import StaticPageArticleSEO from "@/components/Head/StaticPage";
import { TypeSetInnerHTML } from "@/helpers/typeset";
import { isCSRRequest, removeHtmlTags } from "@/utils";
import { FAQPageJsonLd } from "next-seo";
import MetaSeo from "@/components/Head/Meta";
import { getLocaleFromLanguage } from "@/utils/language";
import dynamic from "next/dynamic";
import { StaticPageProps } from "@/components/StaticPage/interface";

const RedirectHome = dynamic(() => import("@/components/Content/RedirectHome"));

const StaticPage = (props: StaticPageProps) => {
  let SEO = null;
  switch (props.source) {
    case "home":
      SEO = <>
        <MetaSeo />
      </>;
      break;
    case "text_solutions_sid99":
      const seoProps: IStaticPageSEO = {
        title: props.data.data.title?.innerHTML?.replace(/(<([^>]+)>)/gi, "") || "",
        description:
          props.data.data.content?.items[0]?.innerHTML?.replace(
            /(<([^>]+)>)/gi,
            ""
          ) || "",
        keywords: [...(props.keywords ?? []), ...(props.data.keywords ?? [])],
        images: props.data.images ?? [],
        updatedAt: props.data.updatedAt || new Date().toISOString(),
      };
      SEO = <StaticPageArticleSEO {...seoProps} />;
      break;
    case "index":
      SEO = <MetaSeo title={props.seo?.title || props.data.data.title?.innerHTML} description={removeHtmlTags(props.seo?.description || props.data.data.content?.items?.[0].innerHTML)} keywords={props.data.keywords} />
      break;
    case "category":
    case "trending-questions":
      SEO = <>
        <MetaSeo title={props.seo?.title} description={removeHtmlTags(props.seo?.description)} keywords={props.keywords} />
        {props?.seo?.faq && <FAQPageJsonLd mainEntity={props.seo.faq} />}
      </>
    default:
      break;
  }

  return (
    <RedirectHome>
      <div className={`static-content body-content ${props?.data.data.props?.className ?? ""}`}>
        {SEO}
        <Section {...props.data.data} />
      </div>
    </RedirectHome>
  );
};


export const getServerSideProps: GetServerSideProps = async (ctx) => {
  let slug = ctx.params?.slug;
  if (!slug || !slug.length) {
    return {
      notFound: true,
    };
  }

  let filter: any,
    keywords: string[] = [];
  if (typeof slug === "string") {
    slug = [slug];
  }
  let redirectSlug: string = slug.join("/");
  switch (slug[0]) {
    case "home":
      filter = {
        source: "home",
        title: "home-page",
      };
      break;
    case "concept":
      filter = {
        source: "text_solutions_sid99",
        studentClass: slug[1],
        subject: ["physics", "chemistry", "biology"].includes(slug[2].split("-")[0]) ? [slug[2], "science"] : slug[2],
        title: slug.slice(3).join("/") || undefined,
      };
      keywords = [`class-${slug[1]}`, slug[2], slug[3]];
      redirectSlug = `class-${slug[1]}/${slug[2]}/concept/${slug.slice(3).join("/")}`;
      break;
    case "study-material":
      filter = {
        source: "liveclass_class_notes_2",
        studentClass: slug[1],
        subject: ["physics", "chemistry", "biology"].includes(slug[2].split("-")[0]) ? [slug[2], "science"] : slug[2],
        title: slug.slice(3).join("/") || undefined,
      };
      keywords = [`class-${slug[1]}`, slug[2], slug[3]];
      redirectSlug = `class-${slug[1]}/${slug[2]}/study-material/${slug.slice(3).join("/")}`;
      break;
    case "index":
      filter = {
        source: "index",
        studentClass: slug[1],
        subject: slug[2] ? (["physics", "chemistry", "biology"].includes(slug[2].split("-")[0]) ? [slug[2], "science"] : slug[2]) : undefined,
        title: slug.slice(3).join("/") || undefined,
      };
      for (const key in filter) {
        if (filter[key] === "default") {
          filter[key] = undefined;
        }
      }
      filter = JSON.parse(JSON.stringify(filter));
      redirectSlug = `class-${slug[1]}/${slug[2]}/${slug.slice(3).join("/")}`;
      break;
    case "category":
      filter = {
        title: slug[1],
        source: "category",
      };
      break;
    case "trending-questions":
      filter = {
        title: slug.join("/"),
        source: "category",
      };
      break;
    default:
      break;
  }

  if (!filter) {
    return {
      notFound: true,
    };
  }

  if (ctx.req.url && !isCSRRequest(ctx.req.url) && ctx.req.url.split("?")[0].toLowerCase() !== ctx.req.url.split("?")[0]) {
    return {
      redirect: {
        destination: `/${redirectSlug.toLowerCase()}?${ctx.req.url.split("?")[1]}`,
        permanent: false,
        basePath: false,
      },
    };
  }

  const locale = Array.isArray(filter.subject) ? getLocaleFromLanguage(filter.subject[0]?.split("-")[1]) : getLocaleFromLanguage(filter.subject?.split("-")[1]);

  const { meta, data } = await getStaticPageData(locale, filter);

  if (!data || !(data as { data: IHTMLSection }).data || meta?.code !== 200) {
    if (meta?.code === 307 && data) {
      return {
        notFound: true,
      };
    }

    return {
      notFound: true,
    };
  }

  await TypeSetInnerHTML((data as { data: IHTMLSection }).data);

  return {
    props: {
      source: filter.source,
      data,
      breadcrumbs: {
        items: (data as { breadcrumbs: any }).breadcrumbs || null,
      },
      seo: (data as { seo: any }).seo || null,
      keywords,
    },
  };
};

export default StaticPage;
